import { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import styled from "styled-components";
import LoadingAnimation from "../../components/animation/LoadingAnimation";
import FilledButton from "../../components/button/FilledButton";
import FloatingChatButton from "../../components/button/FloatingChatButton";
import NotFoundError from "../../components/error/NotFoundError";
import Footer from "../../components/footer/Footer";
import Navbar from "../../components/navbar/Navbar";
import ProductCard from "../../components/product/ProductCard";
import H2 from "../../components/typography/H2";
import { productService } from "../../service/ProductService";
import colors from "../../theme/color/ColorTheme";
import ProductFilter from "./components/ProductFilter";

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Space = styled.div`
  height: 40px;

  @media (max-width: 900px) {
    height: 20px;
  }
`;

const PageTitle = styled.div`
  width: fit-content;
  margin: auto;
  color: ${colors.textSecondary};
  padding: 0 20px;
  text-align: center;
  h2 {
    font-size: 16px;
  }
`;

const Container = styled.div`
  display: flex;
  gap: 20px;
  flex: 1;
  margin: 0 20px;
  position: relative;

  @media (max-width: 900px) {
    margin: 0 15px;
  }
`;

const Left = styled.div`
  width: 280px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 180px;
  height: fit-content;
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  transition: transform 0.3s ease-in-out;

  @media (max-width: 900px) {
    position: fixed;
    top: 0;
    left: 0;
    width: 300px;
    max-height: 100vh;
    height: 100vh;
    z-index: 1000;
    background: white;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    transform: translateX(${(props) => (props.isOpen ? "0" : "-100%")});
    overflow-y: auto;
    padding: 20px;
  }
`;

const Right = styled.div`
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
  gap: 20px;
  row-gap: 30px;
  padding-bottom: 20px;
  justify-items: center;
  @media (max-width: 900px) {
    grid-template-columns: repeat(auto-fill, minmax(330px, 1fr));
    width: 100%;
  }

  @media (max-width: 480px) {
    grid-template-columns: repeat(auto-fill, 1fr);
    gap: 10px;
    row-gap: 20px;
  }
`;

const FilterToggle = styled.button`
  display: none;
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 900;
  background: ${colors.backgroundPrimary};
  border: 1px solid ${colors.border};
  padding: 12px 16px;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  @media (max-width: 900px) {
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${colors.black};
  }
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: 900px) {
    display: ${(props) => (props.isOpen ? "block" : "none")};
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const CloseButton = styled.button`
  display: none;

  @media (max-width: 900px) {
    display: block;
    position: absolute;
    top: 15px;
    right: 15px;
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: ${colors.textSecondary};
  }
`;

const FilterIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 6H20M7 12H17M9 18H15"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const CloseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M18 6L6 18M6 6L18 18"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);
const Flex = styled.div`
  display: flex;
  gap: 10px;
  width: 250px;
  margin: auto;
`;
function SearchResultsPage() {
  const { category, flag } = useParams();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const query = queryParams.get("q");

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageNumber, setPageNumber] = useState(1);
  const [filters, setFilters] = useState({});

  const pageType = flag
    ? "flag"
    : category
      ? "category"
      : query
        ? "search"
        : "default";

  const getTitle = () => {
    if (pageType === "default") return "Latest Products";
    if (pageType === "search") return ` ${query?.toUpperCase() || ""}`;
    if (pageType === "flag") return `${flag?.toUpperCase()} PRODUCTS`;
    return category?.toUpperCase() || "";
  };

  useEffect(() => {
    if (!loading) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      }, 50);
    }
  }, [pageNumber, category, flag, query, loading]);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);

      if (pageType === "flag") {
        productService.searchProducts(
          { query: flag === "sale" ? "sale" : "all", ...filters },
          pageNumber,
          (res) => {
            setProducts(res.products);
            setLoading(false);
          },
          { default: (msg) => alert(msg) },
        );
      } else if (pageType === "search") {
        productService.searchProducts(
          { query, ...filters },
          pageNumber,
          (res) => {
            setProducts(res.products);
            setLoading(false);
          },
          { default: (msg) => alert(msg) },
        );
      } else if (pageType === "category") {
        console.log("Incoming category:", category);
        productService.searchProducts(
          { categories: [category], ...filters },
          pageNumber,
          (res) => {
            setProducts(res.products);
            setLoading(false);
          },
          { default: (msg) => alert(msg) },
        );
      } else {
        productService.searchProducts(
          { query: "all", ...filters },
          pageNumber,
          (res) => {
            setProducts(res.products);
            setLoading(false);
          },
          { default: (msg) => alert(msg) },
        );
      }
    };

    fetchProducts();
  }, [pageType, query, category, flag, pageNumber, filters]);

  // reset page number when filters change
  useEffect(() => {
    setPageNumber(1);
  }, [filters]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters || {});
  };

  return (
    <Wrapper>
      <FloatingChatButton />
      <Navbar />
      <Space />
      <PageTitle>
        <H2>SHOWING RESULTS FOR: {getTitle()}</H2>
      </PageTitle>
      <Space />

      <Container>
        <Left isOpen={isFilterOpen}>
          <CloseButton onClick={() => setIsFilterOpen(false)}>
            <CloseIcon />
          </CloseButton>
          <ProductFilter
            pageNumber={pageNumber}
            onFilterChange={handleFilterChange}
          />
        </Left>

        {loading ? (
          <LoadingAnimation height={"800px"} width={"800px"} />
        ) : (
          <>
            <Right>
              {products.map((product, key) => (
                <ProductCard key={key} product={product} />
              ))}
              {products.length < 1 && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "300px",
                  }}
                >
                  <NotFoundError
                    message={"No Products Found. Continue Shopping"}
                  />
                </div>
              )}
            </Right>
          </>
        )}
      </Container>

      <FilterToggle onClick={() => setIsFilterOpen(!isFilterOpen)}>
        <FilterIcon />
        Filters
      </FilterToggle>

      <Overlay isOpen={isFilterOpen} onClick={() => setIsFilterOpen(false)} />

      <Flex>
        <div style={{ width: "fit-content", margin: "auto" }}>
          <FilledButton
            text={"Back"}
            onClick={() => setPageNumber(pageNumber - 1)}
            disabled={pageNumber <= 1}
          />
        </div>
        <div style={{ width: "fit-content", margin: "auto" }}>
          <FilledButton
            text={"Next"}
            onClick={() => setPageNumber(pageNumber + 1)}
            disabled={products.length === 0 || products.length < 10}
          />
        </div>
      </Flex>

      <Space />
      <Space />
      <Footer />
    </Wrapper>
  );
}

export default SearchResultsPage;
